import React from 'react'
import { format, parse, add } from 'date-fns'
import { HotelsFill, CarsFill, FlightsFill } from '@pcln/brand'
import {
  OriginDestinationData,
  SearchProductData,
  RecentActivityData
} from './types'

const DATE_FORMAT = 'E, MMM dd'

function getDates({
  startDate = '',
  endDate = ''
}: {
  startDate?: string | null
  endDate?: string | null
}) {
  // Dates have already passed
  if (!startDate) {
    const today = new Date()
    const tomorrow = add(today, { days: 1 })
    return [format(today, 'yyyyMMdd'), format(tomorrow, 'yyyyMMdd')]
  }
  // ONE-WAY
  if (!endDate) {
    return [
      format(parse(startDate, 'yyyy-MM-dd', new Date()), 'yyyyMMdd'),
      format(parse(startDate, 'yyyy-MM-dd', new Date()), 'yyyyMMdd')
    ]
  }
  return [
    format(parse(startDate, 'yyyy-MM-dd', new Date()), 'yyyyMMdd'),
    format(parse(endDate, 'yyyy-MM-dd', new Date()), 'yyyyMMdd')
  ]
}

function formatDates({ startDate = '', endDate = '' }) {
  return [
    format(parse(startDate, 'yyyyMMdd', new Date()), DATE_FORMAT),
    format(parse(endDate, 'yyyyMMdd', new Date()), DATE_FORMAT)
  ]
}

function capitalizeLocation(name: string) {
  return name
    .split(' ')
    .map(item => item.charAt(0) + item.slice(1).toLocaleLowerCase())
    .join(' ')
}

function formatLocationName(name: string) {
  const location = name.split(',')
  const capitalizedLocation = capitalizeLocation(location[0])
  return `${capitalizedLocation}, ${location[1] ?? ''}`
}

function formatAirportName(
  airportName: string,
  airportCode: string,
  cityName: string
) {
  const country = cityName.split(',')[1]
  return `${capitalizeLocation(airportName)} (${airportCode}), ${country}`
}

export default function getProductInfo(item: SearchProductData) {
  switch (item.productType) {
    case 'FLY': {
      const [startDate, endDate] = getDates({
        startDate: item.flyOriginDestinationData[0].startDate,
        endDate: item.flyOriginDestinationData[1]?.startDate
      })
      const [formattedStartDate, formattedEndDate] = formatDates({
        startDate,
        endDate
      })
      const {
        originCityData: { cityName: originCityName },
        destinationCityData: { cityName: destinationCityName },
        originAirportData: { airportCode: originAirportCode },
        destinationAirportData: { airportCode: destinationAirportCode }
      }: OriginDestinationData = item.flyOriginDestinationData[0]
        .originDestinationData
      return {
        icon: (
          <div style={{ lineHeight: '0px' }}>
            <FlightsFill size="24px" />
          </div>
        ),
        overline: 'Flight',
        heading: `${
          item.flyTripType === 'ROUND_TRIP'
            ? `${formatLocationName(originCityName)} - `
            : ``
        }${formatLocationName(destinationCityName)}`,
        actionText: `${formattedStartDate}${
          item.flyTripType === 'ROUND_TRIP' ? ` - ${formattedEndDate}` : ``
        }`,
        url: `/m/fly/search/${originAirportCode}-${destinationAirportCode}-${startDate}${
          item.flyTripType === 'ROUND_TRIP'
            ? `/${destinationAirportCode}-${originAirportCode}-${endDate}`
            : ``
        }/?cabin-class=${
          item.flyCabinClass as string
        }&no-date-search=false&num-adults=1&sbsroute=slice1&search-type=10`
      }
    }
    case 'DRIVE': {
      const [startDate, endDate] = getDates({
        startDate: item.driveStartDate,
        endDate: item.driveEndDate
      })
      const [formattedStartDate, formattedEndDate] = formatDates({
        startDate,
        endDate
      })
      const {
        originAirportData: {
          airportCode: originAirportCode,
          airportName: originAirportName
        },
        originCityData: { cityName: originCityName },
        destinationAirportData: {
          airportCode: destinationAirportCode,
          airportName: destinationAirportName
        },
        destinationCityData: { cityName: destinationCityName }
      }: OriginDestinationData = item.driveOriginDestinationData
      return {
        icon: (
          <div style={{ lineHeight: '0px' }}>
            <CarsFill size="24px" />
          </div>
        ),
        overline: 'Cars',
        heading: `${formatAirportName(
          originAirportName,
          originAirportCode,
          originCityName
        )}${
          originAirportCode !== destinationAirportCode
            ? ` - ${formatAirportName(
                destinationAirportName,
                destinationAirportCode,
                destinationCityName
              )}`
            : ''
        }`,
        actionText: `${formattedStartDate} - ${formattedEndDate}`,
        url: `/drive/search/r/${originAirportCode}/${destinationAirportCode}/${startDate}-12:00/${endDate}-12:00/list`
      }
    }
    default: {
      const { cityID, cityName }: { cityID: string; cityName: string } =
        item.stayDestinationData[0].destinationCityData
      const [startDate, endDate] = getDates(item.stayDestinationData[0])
      const [formattedStartDate, formattedEndDate] = formatDates({
        startDate,
        endDate
      })
      return {
        icon: (
          <div style={{ lineHeight: '0px' }}>
            <HotelsFill size="24px" />
          </div>
        ),
        overline: 'Hotels',
        heading: formatLocationName(cityName),
        actionText: `${formattedStartDate} - ${formattedEndDate}`,
        url: `relax/in/${cityID}/from/${startDate}/to/${endDate}/rooms/1/adults/2`
      }
    }
  }
}

export function getRecentActivityData(item: RecentActivityData) {
  const {
    temp_hotel: {
      name,
      neighborhood,
      numberOfReviews,
      starRating,
      averageRating,
      imageURL
    },
    URL,
    checkInDate,
    checkOutDate,
    numberOfAdults,
    numberOfRooms
  } = item
  return {
    hotelName: name,
    neighborhoodName: neighborhood,
    numReviews: numberOfReviews,
    numStars: starRating,
    overallRating: averageRating,
    checkInDate,
    checkOutDate,
    numAdults: numberOfAdults,
    numRooms: numberOfRooms,
    imageSrc: imageURL,
    linkUrl: URL
  }
}
